import type { ICalendarDate, ICompany, ICompanyTariff, ISubscription } from '@types'
import { ESubscriptionType } from '@types'
import { addDays } from 'date-fns'
import { toArray } from './array'

export function calendarDateToDate(date?: ICalendarDate) {
  return date ? new Date(date.year, date.month - 1, date.day) : undefined
}

function isTrialExpired(created: Date | string) {
  const expiration = addDays(new Date(created), 14)
  const expired = expiration < new Date()
  return { expired, expiration }
}

export function getTariff(c: Pick<ICompany, 'subscriptions' | 'created'>): ICompanyTariff | undefined {
  const tariff = c.subscriptions
    ?.map(s => {
      s.nextPayment ??= { year: 0, month: 0, day: 0 }
      return s
    })
    .sort((a, b) => {
      if (a.nextPayment.year !== b.nextPayment.year) {
        return b.nextPayment.year - a.nextPayment.year
      }
      if (a.nextPayment.month !== b.nextPayment.month) {
        return b.nextPayment.month - a.nextPayment.month
      }
      return b.nextPayment.day - a.nextPayment.day
    })
    .find(s =>
      [ESubscriptionType.TARIFF_START, ESubscriptionType.TARIFF_TRIAL, ESubscriptionType.TARIFF_PRO].includes(s.type),
    )

  if (tariff && tariff.nextPayment) {
    tariff._expiration = new Date(
      tariff.nextPayment.year,
      tariff.nextPayment.month - 1,
      tariff.nextPayment.day,
      23,
      59,
      59,
    )
    tariff._active = new Date() < tariff._expiration
    if (tariff._active) {
      return tariff
    }
  }

  if (c.created) {
    const { expired, expiration } = isTrialExpired(c.created)
    if (!expired) {
      return { type: ESubscriptionType.TARIFF_TRIAL, _active: true, _expiration: expiration }
    }
  }

  return tariff
}

export function checkCompanyTariff(c: ICompany, tariff: ESubscriptionType | ESubscriptionType[]) {
  return Boolean(c?._tariff?._active && toArray(tariff).includes(c._tariff.type))
}

export function hasCompanyAccessToPro(c: ICompany) {
  return checkCompanyTariff(c, [ESubscriptionType.TARIFF_TRIAL, ESubscriptionType.TARIFF_PRO])
}

export function hasCompanyTariff(c: ICompany): c is ICompany & { _tariff: ISubscription } {
  return checkCompanyTariff(c, [ESubscriptionType.TARIFF_START, ESubscriptionType.TARIFF_PRO])
}

export function hasCompanyAccessToPlatform(c: ICompany) {
  return c?._tariff?._active
}

export const tariffLabel: Partial<Record<ESubscriptionType, string>> = {
  [ESubscriptionType.TARIFF_START]: 'Cue Start',
  [ESubscriptionType.TARIFF_PRO]: 'Cue Pro',
  [ESubscriptionType.TARIFF_TRIAL]: 'Cue Trial',
}

export function getTariffLabel(tariff: ESubscriptionType): string
export function getTariffLabel(tariff?: ESubscriptionType): string | undefined
export function getTariffLabel(tariff?: ESubscriptionType) {
  return tariff === undefined ? undefined : tariffLabel[tariff] || tariff
}
